import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { PageSpinner } from '@/components/common/PageSpinner';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { ProtectedRoute } from './ProtectedRoute';

/* All pages are lazy-loaded so the initial bundle stays tiny.
 * Route-level chunks line up with the feature folders. */
const HomePage = lazy(() => import('@/pages/HomePage'));
const EventsPage = lazy(() => import('@/pages/EventsPage'));
const EventDetailPage = lazy(() => import('@/pages/EventDetailPage'));
const CommunityPage = lazy(() => import('@/pages/CommunityPage'));
const PrayerTimesPage = lazy(() => import('@/pages/PrayerTimesPage'));
const DonatePage = lazy(() => import('@/pages/DonatePage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const AdsPage = lazy(() => import('@/pages/AdsPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const AdminDashboardHomePage = lazy(() => import('@/pages/admin/AdminDashboardHomePage'));
const AdminMembersPage = lazy(() => import('@/pages/admin/AdminMembersPage'));
const AdminMessagesPage = lazy(() => import('@/pages/admin/AdminMessagesPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<PageSpinner />}>{node}</Suspense>
);

const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: withSuspense(<HomePage />) },
      { path: '/events', element: withSuspense(<EventsPage />) },
      { path: '/events/:slug', element: withSuspense(<EventDetailPage />) },
      { path: '/community', element: withSuspense(<CommunityPage />) },
      { path: '/prayer-times', element: withSuspense(<PrayerTimesPage />) },
      { path: '/donate', element: withSuspense(<DonatePage />) },
      { path: '/ads', element: withSuspense(<AdsPage />) },
      {
        path: '/education',
        element: (
          <PlaceholderPage
            phase="PHASE 4"
            title="Academy"
            description="Courses, instructor profiles, enrollment, and protected materials are scheduled for Phase 4."
          />
        ),
      },
      {
        path: '/services',
        element: (
          <PlaceholderPage
            phase="PHASE 4"
            title="Services"
            description="Marriage, funeral, counselling, and Nikkah services pages ship in Phase 4."
          />
        ),
      },
      { path: '/about', element: withSuspense(<AboutPage />) },
      { path: '/contact', element: withSuspense(<ContactPage />) },
      {
        element: <ProtectedRoute />,
        children: [{ path: '/profile', element: withSuspense(<ProfilePage />) }],
      },
    ],
  },
  {
    element: <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']} />,
    children: [
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/dashboard" replace /> },
          { path: 'dashboard', element: withSuspense(<AdminDashboardHomePage />) },
          { path: 'members', element: withSuspense(<AdminMembersPage />) },
          { path: 'messages', element: withSuspense(<AdminMessagesPage />) },
        ],
      },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: '/auth/login', element: withSuspense(<LoginPage />) },
      { path: '/auth/register', element: withSuspense(<RegisterPage />) },
    ],
  },
  { path: '*', element: withSuspense(<NotFoundPage />) },
]);

export function AppRouter() {
  // Opt in early to React Router v7's startTransition-wrapped state updates
  // (suppresses the v7_startTransition future-flag warning). This flag is
  // read by RouterProvider, not createBrowserRouter.
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
